import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, fmtDate, errMsg, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { useAuth } from "@finance/context/AuthContext";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@finance/components/ui/tabs";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { toast } from "sonner";
import { Plus, DownloadSimple, Trash, CheckCircle, WarningCircle, FilePdf, CalendarBlank } from "@phosphor-icons/react";
import { DocNumberField } from "@finance/components/DocNumberField";
import { DocumentPreview } from "@finance/components/DocumentPreview";

function ym(d) { return d.toISOString().slice(0, 7); }

export default function TaxSales() {
  const { user } = useAuth();
  const canRecon = !!user && user.email === "finance@somcoregroup.com";
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [register, setRegister] = useState("sales");
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [month, setMonth] = useState(ym(new Date()));
  const [view, setView] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [salePreview, setSalePreview] = useState(false);
  const [vatPreview, setVatPreview] = useState(false);
  const [reconRow, setReconRow] = useState(null);
  const [reconDate, setReconDate] = useState("");

  const load = useCallback(() => {
    api.get("/tax-sales", { params: { month } }).then((r) => setSales(r.data));
    api.get("/tax-purchases", { params: { month } }).then((r) => setPurchases(r.data));
    api.get("/tax-sales/vat-summary", { params: { month } }).then((r) => setSummary(r.data));
  }, [month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  const excl = Number(form.amount_exclusive) || 0;
  const vat = excl * 0.18;

  const save = async () => {
    if (!form.date || !form.amount_exclusive) { toast.error("Date and exclusive amount required"); return false; }
    try {
      await api.post("/tax-sales", { ...form, amount_exclusive: Number(form.amount_exclusive), zero_rated_amount: Number(form.zero_rated_amount) || 0 });
      toast.success("Tax sale recorded & posted to GL");
      setOpen(false); setForm({}); load();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this tax sale? Its ledger entry will be reversed.")) return;
    await api.delete(`/tax-sales/${id}`);
    toast.success("Deleted & GL reversed");
    load();
  };

  const openRecon = (r) => { setReconRow(r); setReconDate(r.vat_return_date || ""); };

  const saveReconDate = async (override) => {
    const val = override !== undefined ? override : reconDate;
    const r = reconRow;
    try {
      const url = register === "purchases" ? `/tax-purchases/${r.id}/vat-return-date` : `/tax-sales/${r.id}/vat-return-date`;
      await api.put(url, { vat_return_date: val || null, source: r.source });
      toast.success(val ? "VAT Return Reflected Date saved" : "Reconciliation cleared");
      setReconRow(null); setReconDate(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const activeRows = register === "purchases" ? purchases : sales;
  const salesUnrec = sales.filter((s) => !s.reconciled).length;
  const purchUnrec = purchases.filter((p) => !p.reconciled).length;
  const activeUnrec = register === "purchases" ? purchUnrec : salesUnrec;
  const filtered = view === "unreconciled" ? activeRows.filter((r) => !r.reconciled) : activeRows;

  const reconcileCell = (r) => {
    const badge = (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${r.reconciled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
        {r.reconciled ? <CheckCircle size={13} weight="fill" /> : null}{r.recon_status}
      </span>
    );
    if (!canRecon) return <div data-testid={`recon-status-${r.id}`} title={r.vat_return_date ? `Reflected in TRA return on ${fmtDate(r.vat_return_date)}` : "Not yet reflected in a filed VAT return"}>{badge}</div>;
    return (
      <button
        data-testid={`recon-edit-${r.id}`}
        onClick={() => openRecon(r)}
        title="Set VAT Return Reflected Date"
        className="inline-flex items-center gap-1.5 transition-opacity hover:opacity-75"
      >
        {badge}<CalendarBlank size={14} className="text-gray-400" />
      </button>
    );
  };

  const cols = [
    { key: "date", label: "Date" }, { key: "tax_receipt_code", label: "Receipt Code" },
    { key: "receipt_time", label: "Time" }, { key: "client_name", label: "Client" },
    { key: "amount_exclusive", label: "Exclusive", format: fmtNum },
    { key: "vat_amount", label: "VAT", format: fmtNum },
    { key: "zero_rated_amount", label: "Zero Rated", format: fmtNum },
    { key: "amount_inclusive", label: "Inclusive", format: fmtNum },
    { key: "tax_invoice_no", label: "Tax Invoice" }, { key: "recon_status", label: "VAT Return" },
  ];

  const purchCols = [
    { key: "date", label: "Date" },
    { key: "source", label: "Source", format: (v) => (v || "").replace("_", " ") },
    { key: "source_doc", label: "Source Document" },
    { key: "vendor_name", label: "Vendor" },
    { key: "amount_exclusive", label: "Exclusive", format: fmtNum },
    { key: "vat_amount", label: "VAT (Input)", format: fmtNum },
    { key: "amount_inclusive", label: "Inclusive", format: fmtNum },
    { key: "vat_return_date", label: "VAT Return Date", format: (v) => (v ? fmtDate(v) : "") },
    { key: "recon_status", label: "VAT Return" },
  ];

  return (
    <Layout>
      <PageHeader
        title="VAT"
        subtitle="Sales & purchases VAT registers with monthly VAT summary"
        actions={
          <>
            <Input type="month" data-testid="vat-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
            <Button variant="outline" data-testid="vat-summary-pdf" onClick={() => setVatPreview(true)}><FilePdf size={16} className="mr-1" />VAT Summary</Button>
            <Button variant="outline" data-testid="export-tax-sales" onClick={() => register === "purchases" ? exportToCsv(`vat-purchases-${month}`, purchases, purchCols) : exportToCsv(`tax-sales-${month}`, sales, cols)}><DownloadSimple size={16} className="mr-1" />Export {register === "purchases" ? "Purchases" : "Sales"}</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-tax-sale" onClick={() => { setForm({ date: new Date().toISOString().slice(0, 10), tally_status: "Pending" }); setOpen(true); }}><Plus size={16} className="mr-1" />New Sale</Button>
          </>
        }
      />
      <div className="p-8">
        {summary && (
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
            <SummaryCard label="Sales (Incl.)" value={fmtNum(summary.total_inclusive, "TZS")} testid="vat-incl" />
            <SummaryCard label="Sales (Excl.)" value={fmtNum(summary.total_exclusive, "TZS")} testid="vat-excl" />
            <SummaryCard label="VAT Output" value={fmtNum(summary.vat_output ?? summary.total_vat_output, "TZS")} accent testid="vat-output" />
            <SummaryCard label="VAT Input" value={fmtNum(summary.vat_input, "TZS")} testid="vat-input" />
            <SummaryCard label="Net VAT Position" value={fmtNum(summary.net_vat_position, "TZS")} accent testid="vat-net" />
            <SummaryCard label="Zero Rated" value={fmtNum(summary.total_zero_rated, "TZS")} testid="vat-zero" />
            <SummaryCard label="Records" value={`${summary.reconciled_count}/${summary.count} tallied`} testid="vat-count" />
          </div>
        )}
        {summary && <div className="-mt-4 mb-3 text-xs text-gray-400">VAT Output = all client sales · VAT Input = supplier invoices + expenses · Net VAT Position = Output − Input (payable to TRA if positive).</div>}
        {summary && (summary.ties_to_gl ? (
          <div data-testid="vat-tieout-banner" className="mb-6 flex items-center gap-2 rounded-sm border border-green-200 bg-green-50 px-4 py-2.5 text-sm font-medium text-green-700">
            <CheckCircle size={18} weight="fill" /> Ties to GL — the VAT register's Output and Input reconcile to the General Ledger.
          </div>
        ) : (
          <div data-testid="vat-tieout-banner" className="mb-6 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-700">
            <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
            <div><b>Does not tie to GL.</b>{" "}
              {!summary.output_ties && <span>Output: register {fmtNum(summary.total_vat_output, "TZS")} vs GL {fmtNum(summary.vat_output, "TZS")}. </span>}
              {!summary.input_ties && <span>Input: register {fmtNum(summary.register_vat_input, "TZS")} vs GL {fmtNum(summary.vat_input, "TZS")}.</span>}
            </div>
          </div>
        ))}
        <Tabs value={register} onValueChange={(v) => { setRegister(v); setView("all"); }} className="mb-3">
          <TabsList data-testid="vat-register-tabs">
            <TabsTrigger value="sales" data-testid="vat-tab-sales">Sales Register ({sales.length})</TabsTrigger>
            <TabsTrigger value="purchases" data-testid="vat-tab-purchases">Purchases &amp; Expenses ({purchases.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mb-4 flex items-center gap-2">
          <Button size="sm" variant={view === "all" ? "default" : "outline"} data-testid="vat-filter-all" onClick={() => setView("all")}>All ({activeRows.length})</Button>
          <Button size="sm" variant={view === "unreconciled" ? "default" : "outline"} data-testid="vat-filter-unreconciled" onClick={() => setView("unreconciled")}>Unreconciled ({activeUnrec})</Button>
        </div>
        {register === "sales" ? (
          <div className="max-h-[calc(100vh-18rem)] overflow-auto rounded-sm border border-gray-200 bg-white" data-testid="vat-sales-table">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50"><tr className="lx-label border-b border-gray-200">
                <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Receipt Code</th>
                <th className="px-3 py-2 text-left">Client</th><th className="px-3 py-2 text-right">Exclusive</th>
                <th className="px-3 py-2 text-right">VAT</th><th className="px-3 py-2 text-right">Inclusive</th>
                <th className="px-3 py-2 text-left">Tax Invoice</th><th className="px-3 py-2 text-left">VAT Return</th>
                <th className="px-3 py-2 text-right">·</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">{view === "unreconciled" ? `No unreconciled sales for ${month}. All tied out ✓` : `No sales recorded for ${month}.`}</td></tr>}
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`tax-sale-row-${s.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 lx-num">{fmtDate(s.date)}</td>
                    <td className="px-3 py-2 font-mono-num">{s.tax_receipt_code || "—"}</td>
                    <td className="px-3 py-2">{s.client_name || "—"}</td>
                    <td className="px-3 py-2 text-right lx-num">{fmtNum(s.amount_exclusive, "TZS")}</td>
                    <td className="px-3 py-2 text-right lx-num">{fmtNum(s.vat_amount, "TZS")}</td>
                    <td className="px-3 py-2 text-right lx-num">{fmtNum(s.amount_inclusive, "TZS")}</td>
                    <td className="px-3 py-2 font-mono-num">{s.tax_invoice_no || "—"}</td>
                    <td className="px-3 py-2">{reconcileCell(s)}</td>
                    <td className="px-3 py-2 text-right">{s.source === "client_invoice" ? <span className="text-[11px] text-gray-400" data-testid={`tax-sale-invoice-src-${s.id}`}>from invoice</span> : <button className="text-gray-400 hover:text-red-500" data-testid={`tax-sale-delete-${s.id}`} onClick={() => del(s.id)}><Trash size={15} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="max-h-[calc(100vh-18rem)] overflow-auto rounded-sm border border-gray-200 bg-white" data-testid="vat-purchases-table">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50"><tr className="lx-label border-b border-gray-200">
                <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Source Document</th>
                <th className="px-3 py-2 text-left">Vendor</th><th className="px-3 py-2 text-right">Exclusive</th>
                <th className="px-3 py-2 text-right">VAT (Input)</th><th className="px-3 py-2 text-right">Inclusive</th>
                <th className="px-3 py-2 text-left">VAT Return</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">{view === "unreconciled" ? `No unreconciled purchases for ${month}. All tied out ✓` : `No VAT-bearing purchases or expenses for ${month}.`}</td></tr>}
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`tax-purchase-row-${p.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 lx-num">{fmtDate(p.date)}</td>
                    <td className="px-3 py-2"><span className="mr-1 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-500">{(p.source || "").replace("_", " ")}</span>{p.source_doc || "—"}</td>
                    <td className="px-3 py-2">{p.vendor_name || "—"}</td>
                    <td className="px-3 py-2 text-right lx-num">{fmtNum(p.amount_exclusive, "TZS")}</td>
                    <td className="px-3 py-2 text-right lx-num">{fmtNum(p.vat_amount, "TZS")}</td>
                    <td className="px-3 py-2 text-right lx-num">{fmtNum(p.amount_inclusive, "TZS")}</td>
                    <td className="px-3 py-2">{reconcileCell(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="tax-sale-dialog">
          <DialogHeader><DialogTitle className="font-display">New Tax Sale</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="lx-label">Date</Label><DateField data-testid="ts-date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
            <div><Label className="lx-label">Receipt Time</Label><Input data-testid="ts-time" value={form.receipt_time || ""} onChange={(e) => setForm({ ...form, receipt_time: e.target.value })} className="mt-1" placeholder="10:22" /></div>
            <div><Label className="lx-label">Tax Receipt Code</Label><Input data-testid="ts-code" value={form.tax_receipt_code || ""} onChange={(e) => setForm({ ...form, tax_receipt_code: e.target.value })} className="mt-1" /></div>
            <DocNumberField docKey="tax_invoice" label="Tax Invoice No" testid="ts-invoice-no" value={form.tax_invoice_no} onChange={(v) => setForm({ ...form, tax_invoice_no: v })} />
            <div className="col-span-2">
              <Label className="lx-label">Client</Label>
              <div className="mt-1 flex items-center gap-1">
                <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? null : v })}>
                  <SelectTrigger className="flex-1" data-testid="ts-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <QuickAddButton type="client" onCreated={(c) => { setClients((s) => [...s, c]); setForm((s) => ({ ...s, client_id: c.id })); }} />
              </div>
            </div>
            <div><Label className="lx-label">Amount Exclusive</Label><Input type="number" data-testid="ts-exclusive" value={form.amount_exclusive || ""} onChange={(e) => setForm({ ...form, amount_exclusive: e.target.value })} className="mt-1" /></div>
            <div><Label className="lx-label">Zero Rated</Label><Input type="number" value={form.zero_rated_amount || ""} onChange={(e) => setForm({ ...form, zero_rated_amount: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2 rounded-sm bg-gray-50 p-3 text-sm">
              <div className="flex justify-between"><span className="lx-label">VAT (18%)</span><span className="lx-num">{fmtNum(vat, "TZS")}</span></div>
              <div className="flex justify-between font-medium"><span className="lx-label">Inclusive</span><span className="lx-num text-[#1B4383]">{fmtNum(excl + vat + (Number(form.zero_rated_amount) || 0), "TZS")}</span></div>
            </div>
            <div className="col-span-2"><Label className="lx-label">Narration</Label><Textarea data-testid="ts-narration" value={form.narration || ""} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="mt-1" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" data-testid="preview-tax-sale" onClick={() => setSalePreview(true)}>Preview</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-tax-sale" onClick={save}>Save & Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentPreview
        open={salePreview} onOpenChange={setSalePreview}
        title="Tax Sale"
        onConfirm={async () => { if (await save()) setSalePreview(false); }}
        confirmLabel="Confirm & Post"
        meta={[
          ["Date", form.date],
          ["Receipt Code", form.tax_receipt_code],
          ["Client", (clients.find((c) => c.id === form.client_id) || {}).name],
          ["Tax Invoice No", form.tax_invoice_no],
        ]}
        columns={[{ key: "line", label: "Line" }, { key: "amt", label: "Amount", align: "right" }]}
        rows={[
          { line: "Exclusive", amt: fmtNum(excl) },
          { line: "VAT (18%)", amt: fmtNum(vat) },
          { line: "Zero Rated", amt: fmtNum(Number(form.zero_rated_amount) || 0) },
        ]}
        totals={[["Inclusive", fmtNum(excl + vat + (Number(form.zero_rated_amount) || 0)), true]]}
      />

      {summary && (
        <DocumentPreview
          open={vatPreview} onOpenChange={setVatPreview}
          title={`VAT Summary — ${month}`}
          onConfirm={() => { setVatPreview(false); openPdf(`pdf/vat-return?month=${month}`); }}
          confirmLabel="Generate PDF"
          meta={[
            ["Period", month],
            ["Records", `${summary.reconciled_count}/${summary.count} tallied`],
          ]}
          columns={[{ key: "line", label: "Line" }, { key: "amt", label: "Amount", align: "right" }]}
          rows={[
            { line: "Sales (Exclusive)", amt: fmtNum(summary.total_exclusive) },
            { line: "Sales (Inclusive)", amt: fmtNum(summary.total_inclusive) },
            { line: "VAT Output (all client sales)", amt: fmtNum(summary.vat_output ?? summary.total_vat_output) },
            { line: "VAT Input (supplier invoices + expenses)", amt: fmtNum(summary.vat_input) },
            { line: "Zero Rated", amt: fmtNum(summary.total_zero_rated) },
          ]}
          totals={[["Net VAT Position", fmtNum(summary.net_vat_position), true]]}
          note="Net VAT Position = Output − Input (payable to TRA if positive; refundable if negative)."
        />
      )}

      <Dialog open={!!reconRow} onOpenChange={(o) => { if (!o) setReconRow(null); }}>
        <DialogContent className="max-w-md" data-testid="recon-dialog">
          <DialogHeader><DialogTitle className="font-display">VAT Return Reflected Date</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Enter the date this transaction was reflected in the filed TRA VAT return. Leave blank (or Clear) to keep it unreconciled.</p>
            {reconRow && (
              <div className="rounded-sm bg-gray-50 p-3 text-sm">
                <div className="flex justify-between"><span className="lx-label">Entered in system</span><span className="lx-num">{fmtDate(reconRow.entered_date || reconRow.date)}</span></div>
                <div className="mt-1 flex justify-between"><span className="lx-label">Amount (VAT)</span><span className="lx-num text-[#1B4383]">{fmtNum(reconRow.vat_amount, "TZS")}</span></div>
              </div>
            )}
            <div>
              <Label className="lx-label">VAT Return Reflected Date</Label>
              <DateField data-testid="recon-date-input" value={reconDate} onChange={(e) => setReconDate(e.target.value)} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="recon-clear" onClick={() => saveReconDate("")}>Clear</Button>
            <Button variant="outline" onClick={() => setReconRow(null)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="recon-save" onClick={() => saveReconDate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-gray-200 bg-white p-4">
      <div className="lx-label">{label}</div>
      <div className={`mt-2 font-display text-lg font-bold lx-num ${accent ? "text-[#1B4383]" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}
